import { Injectable } from '@angular/core';
import { CanActivate, CanLoad, Router } from '@angular/router';

import { RoutePaths } from '../shared/constants/route-paths.constant';
import { BrowserType, BrowserVersion, DeviceDetector, DeviceInfo, PlatformType } from '../shared/services/device-detector.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { BaseRouteGuard } from './base.route-guard';

@Injectable({
    providedIn: 'root'
})
export class FluidContentHostRouteGuard extends BaseRouteGuard implements CanActivate, CanLoad {
    private readonly fluidSupportEnabled: boolean;
    private readonly isSupportedBrowser: boolean;
    private readonly unsupportedBrowsers: Map<BrowserType, Array<PlatformType>>;
    private readonly minimumBrowserVersions: Map<BrowserType, BrowserVersion>;

    constructor(
        capabilitiesService: CapabilitiesService,
        router: Router,
        deviceDetector: DeviceDetector
    ) {
        super(router, capabilitiesService);
        this.unsupportedBrowsers = new Map();
        this.setupUnsupportedBrowsers();
        this.minimumBrowserVersions = new Map();
        this.setupMinimumBrowserVersions();
        this.fluidSupportEnabled = capabilitiesService.capabilities.fluidSupportEnabled;
        this.isSupportedBrowser = this.supportedBrowserCheck(deviceDetector.getDeviceInfo());
    }

    public canActivate() {
        if (this.isSupportedBrowser) {
            return this.checkEnabledAndRedirect(this.fluidSupportEnabled);
        }
        return this.checkEnabledAndRedirect(false, RoutePaths.FluidFailurePath, undefined, true);
    }

    public canLoad() {
        if (this.isSupportedBrowser) {
            return this.checkEnabledAndRedirect(this.fluidSupportEnabled);
        }
        return this.checkEnabledAndRedirect(false, RoutePaths.FluidFailurePath, undefined, true);
    }

    public supportedBrowserCheck(deviceInfo: DeviceInfo): boolean {
        const blacklistedPlatforms = this.unsupportedBrowsers.get(deviceInfo.browser.browserType);
        const minimumBrowserVersion = this.minimumBrowserVersions.get(deviceInfo.browser.browserType);
        return !blacklistedPlatforms?.includes(deviceInfo.platform.platformType) && (deviceInfo.browserVersion.compareTo(minimumBrowserVersion) !== -1);
    }

    private setupUnsupportedBrowsers() {
        this.unsupportedBrowsers.set(BrowserType.Edge, [
            PlatformType.Chromebook
        ]);
        this.unsupportedBrowsers.set(BrowserType.EdgeLegacy, [
            PlatformType.PC,
            PlatformType.Mac,
            PlatformType.Chromebook,
            PlatformType.iPhone,
            PlatformType.iPad,
            PlatformType.AndroidPhone,
            PlatformType.AndroidTablet,
            PlatformType.Linux
        ]);
        this.unsupportedBrowsers.set(BrowserType.Firefox, [
            PlatformType.Chromebook
        ]);
        this.unsupportedBrowsers.set(BrowserType.Safari, [
            PlatformType.PC,
            PlatformType.Chromebook,
            PlatformType.AndroidTablet,
            PlatformType.AndroidPhone,
            PlatformType.Linux
        ]);
        this.unsupportedBrowsers.set(BrowserType.IE, [
            PlatformType.PC,
            PlatformType.Mac,
            PlatformType.Chromebook,
            PlatformType.iPhone,
            PlatformType.iPad,
            PlatformType.AndroidPhone,
            PlatformType.AndroidTablet,
            PlatformType.Linux
        ]);
        this.unsupportedBrowsers.set(BrowserType.Opera, [
            PlatformType.Chromebook
        ]);
        this.unsupportedBrowsers.set(BrowserType.WebView, [
            PlatformType.PC,
            PlatformType.Mac,
            PlatformType.Chromebook,
            PlatformType.iPhone,
            PlatformType.iPad,
            PlatformType.AndroidPhone,
            PlatformType.AndroidTablet,
            PlatformType.Linux
        ]);
    }

    private setupMinimumBrowserVersions() {
        this.minimumBrowserVersions.set(BrowserType.Edge, new BrowserVersion(80, 0, 0));
        this.minimumBrowserVersions.set(BrowserType.Chrome, new BrowserVersion(80, 0, 0));
        this.minimumBrowserVersions.set(BrowserType.Safari, new BrowserVersion(13, 4, 0));
        this.minimumBrowserVersions.set(BrowserType.Firefox, new BrowserVersion(78, 0, 0));
    }
}
